import { useEffect, useState, type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { API_AUTH_LOST_EVENT, API_AUTH_RESTORED_EVENT, isApiAuthLost } from '../shared/auth/apiAuthSession';

type Props = {
  children: ReactNode;
};

/**
 * Блокирует shell при 401 API (например, отмена Basic Auth при живом SPA в памяти).
 */
export function ApiAuthGate({ children }: Props) {
  const { t } = useTranslation('layout');
  const [blocked, setBlocked] = useState(isApiAuthLost());

  useEffect(() => {
    const onLost = () => setBlocked(true);
    const onRestored = () => setBlocked(false);
    window.addEventListener(API_AUTH_LOST_EVENT, onLost);
    window.addEventListener(API_AUTH_RESTORED_EVENT, onRestored);
    return () => {
      window.removeEventListener(API_AUTH_LOST_EVENT, onLost);
      window.removeEventListener(API_AUTH_RESTORED_EVENT, onRestored);
    };
  }, []);

  if (!blocked) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full rounded-lg border border-border bg-card p-6 text-center space-y-4">
        <h1 className="text-lg font-semibold text-foreground">
          {t('apiAuthGate.title', { defaultValue: 'Требуется авторизация' })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('apiAuthGate.message', {
            defaultValue:
              'Сессия доступа к API недействительна. Обновите страницу и введите пароль периметра снова.',
          })}
        </p>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          onClick={() => window.location.reload()}
        >
          {t('apiAuthGate.reload', { defaultValue: 'Обновить страницу' })}
        </button>
      </div>
    </div>
  );
}
